import { api } from '../utils/apiClient'

interface IntercomSettings {
  app_id: string
  api_base?: string
  intercom_user_jwt: string // JWT token containing all user data securely
}

class IntercomService {
  private booted: boolean = false
  private settings: IntercomSettings | null = null

  async initialize() {
    if (!import.meta.env.VITE_INTERCOM_APP_ID) {
      console.warn('Intercom: No VITE_INTERCOM_APP_ID found, aborting initialization')
      return
    }

    try {
      // Get JWT settings from backend
      const response = await api.getIntercomSettings()

      const { app_id, api_base, intercom_user_jwt } = response.data

      if (!app_id || !intercom_user_jwt) {
        console.error('Intercom: Missing app_id or intercom_user_jwt in response:', response.data)
        return
      }

      this.settings = {
        app_id,
        api_base: api_base || "https://api-iam.intercom.io",
        intercom_user_jwt
      }

      this.boot()
    } catch (error) {
      console.error('Intercom: Failed to initialize:', error)
      if (error.response) {
        console.error('Intercom: Error response:', error.response.status, error.response.data)
      }
    }
  }

  boot() {
    if (!this.settings || this.booted) {
      console.warn('Intercom: boot() aborted - settings missing or already booted')
      return
    }

    window.Intercom('boot', this.settings)
    this.booted = true
  }

  update(settings?: Partial<IntercomSettings>) {
    if (!this.booted) return

    const updateSettings = settings || this.settings
    window.Intercom('update', updateSettings)
  }

  shutdown() {
    if (!this.booted) return

    window.Intercom('shutdown')
    this.booted = false
    this.settings = null
  }

  // Custom event tracking
  trackEvent(eventName: string, metadata?: Record<string, any>) {
    if (!this.booted) return

    window.Intercom('trackEvent', eventName, metadata)
  }

  // Show/hide messenger
  show() {
    if (!this.booted) return
    window.Intercom('show')
  }

  hide() {
    if (!this.booted) return
    window.Intercom('hide')
  }
}

export default new IntercomService()
